import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { calcMaterials } from './MaterialCalculator';
/**
 * SimpleAreaCalculator
 * --------------------
 * A stripped-down area calculator used inline on the Product page. The
 * product context already tells us the MATERIAL — so we drop the
 * material / project-type pickers from the full MaterialCalculator and
 * just ask the shopper for shape + dimensions + depth.
 *
 * Live yardage updates as the user types. An "Apply" button hands the
 * computed yard count back to the parent (which then snaps the
 * quantity selector or splits multi-loads, exactly like the full
 * calculator did).
 */
type Shape = 'rect' | 'circle' | 'tri' | 'other';
type Props = {
  /** Material slug, used to look up density from calcMaterials. */
  materialSlug?: string;
  /** Fired when the shopper confirms — yard count is already rounded up. */
  onApply: (yards: number) => void;
};
export function SimpleAreaCalculator({
  materialSlug,
  onApply
}: Props) {
  const [shape, setShape] = useState<Shape>('rect');
  const [unit, setUnit] = useState<'ft' | 'm'>('ft');
  const [depthUnit, setDepthUnit] = useState<'in' | 'cm'>('in');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [diameter, setDiameter] = useState('');
  const [base, setBase] = useState('');
  const [height, setHeight] = useState('');
  const [depth, setDepth] = useState('3');
  // Pull density from the shared materials registry so the tonnage /
  // yardage math matches the full calculator. Falls back to a 1.4
  // average if the slug isn't in the list.
  const density = useMemo(() => {
    const m = calcMaterials.find(x => x.id === materialSlug);
    return m ? m.density : 1.4;
  }, [materialSlug]);
  // Reset stale dimension state when the shape changes — keeps the
  // result from carrying over between shapes.
  useEffect(() => {
    setLength('');
    setWidth('');
    setDiameter('');
    setBase('');
    setHeight('');
  }, [shape]);
  const yards = useMemo(() => {
    const d = parseFloat(depth);
    if (isNaN(d) || d <= 0) return 0;
    const mToFt = 3.28084;
    const toFt = (v: string) => {
      const n = parseFloat(v) || 0;
      return unit === 'm' ? n * mToFt : n;
    };
    let areaSqFt = 0;
    if (shape === 'rect' || shape === 'other') {
      const l = toFt(length);
      const w = toFt(width);
      if (l <= 0 || w <= 0) return 0;
      areaSqFt = l * w;
    } else if (shape === 'circle') {
      const dia = toFt(diameter);
      if (dia <= 0) return 0;
      areaSqFt = Math.PI * (dia / 2) ** 2;
    } else if (shape === 'tri') {
      const b = toFt(base);
      const h = toFt(height);
      if (b <= 0 || h <= 0) return 0;
      areaSqFt = 0.5 * b * h;
    }
    const depthFt = depthUnit === 'cm' ? d / 30.48 : d / 12;
    const cubicYards = areaSqFt * depthFt / 27;
    return Math.ceil(cubicYards * 10) / 10;
  }, [shape, unit, depthUnit, length, width, diameter, base, height, depth]);
  const tons = Math.ceil(yards * density * 10) / 10;
  const canApply = yards > 0;
  const shapes: {
    id: Shape;
    label: string;
  }[] = [{
    id: 'rect',
    label: 'Rect'
  }, {
    id: 'circle',
    label: 'Circle'
  }, {
    id: 'tri',
    label: 'Tri'
  }, {
    id: 'other',
    label: 'Other'
  }];
  const inputCls = 'w-full px-4 py-2.5 bg-white dark:bg-[#2e2e2e] border border-[#d4ddcf] dark:border-[#4a4a4a] rounded-full text-sm text-stone-900 dark:text-white placeholder-stone-400 focus:ring-2 focus:ring-[#3d7530] focus:border-transparent outline-none transition-all';
  const labelCls = 'block text-xs font-semibold text-stone-700 dark:text-stone-300 mb-1.5';
  return <div className="rounded-2xl border border-[#d4ddcf] dark:border-[#4a4a4a] bg-[#f2f6ec] dark:bg-[#363636] p-5" data-id="element-1971">
      <p className="text-xs font-bold tracking-widest text-stone-500 dark:text-stone-400 uppercase mb-3" data-id="element-1972">
        Area Shape
      </p>

      {/* Shape pills */}
      <div className="flex gap-2 mb-5" data-id="element-1973">
        {shapes.map(({
        id,
        label
      }) => <button key={id} type="button" onClick={() => setShape(id)} className={`relative flex-1 min-w-0 py-2.5 px-2 rounded-full text-sm font-bold transition-colors z-10 ${shape === id ? 'text-white' : 'text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-white'}`} data-id="element-1974">
            {shape === id && <motion.div layoutId="simpleShape" className="absolute inset-0 bg-[#3d7530] rounded-full -z-10" transition={{
          type: 'spring',
          bounce: 0.2,
          duration: 0.5
        }} data-id="element-1975" />}
            {label}
          </button>)}
      </div>

      {/* Inline unit toggles */}
      <div className="flex items-center gap-5 mb-5 text-xs" data-id="element-1976">
        <div className="flex items-center gap-2" data-id="element-1977">
          <span className="font-semibold text-stone-600 dark:text-stone-300" data-id="element-1978">
            Units:
          </span>
          <div className="flex bg-[#e8ede4] dark:bg-[#2e2e2e] rounded-full p-0.5 border border-[#d4ddcf] dark:border-[#4a4a4a]" data-id="element-1979">
            {(['ft', 'm'] as const).map(u => <button key={u} type="button" onClick={() => setUnit(u)} className={`px-2.5 py-1 text-xs font-bold rounded-full transition-colors ${unit === u ? 'bg-[#3d7530] text-white' : 'text-stone-500 dark:text-stone-400'}`} data-id="element-1980">
                {u}
              </button>)}
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-1981">
          <span className="font-semibold text-stone-600 dark:text-stone-300" data-id="element-1982">
            Depth:
          </span>
          <div className="flex bg-[#e8ede4] dark:bg-[#2e2e2e] rounded-full p-0.5 border border-[#d4ddcf] dark:border-[#4a4a4a]" data-id="element-1983">
            {(['in', 'cm'] as const).map(u => <button key={u} type="button" onClick={() => setDepthUnit(u)} className={`px-2.5 py-1 text-xs font-bold rounded-full transition-colors ${depthUnit === u ? 'bg-[#3d7530] text-white' : 'text-stone-500 dark:text-stone-400'}`} data-id="element-1984">
                {u}
              </button>)}
          </div>
        </div>
      </div>

      {/* Dimension inputs — change based on shape */}
      <AnimatePresence mode="wait" data-id="element-1985">
        {(shape === 'rect' || shape === 'other') && <motion.div key="rect" initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} className="grid grid-cols-3 gap-3" data-id="element-1986">
            <div data-id="element-1987">
              <label className={labelCls} data-id="element-1988">Length ({unit})</label>
              <input type="number" value={length} onChange={e => setLength(e.target.value)} placeholder="0" className={inputCls} min="0" step="any" data-id="element-1989" />
            </div>
            <div data-id="element-1990">
              <label className={labelCls} data-id="element-1991">Width ({unit})</label>
              <input type="number" value={width} onChange={e => setWidth(e.target.value)} placeholder="0" className={inputCls} min="0" step="any" data-id="element-1992" />
            </div>
            <div data-id="element-1993">
              <label className={labelCls} data-id="element-1994">Depth ({depthUnit})</label>
              <input type="number" value={depth} onChange={e => setDepth(e.target.value)} placeholder="0" className={inputCls} min="0" step="any" data-id="element-1995" />
            </div>
          </motion.div>}
        {shape === 'circle' && <motion.div key="circ" initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} className="grid grid-cols-2 gap-3" data-id="element-1996">
            <div data-id="element-1997">
              <label className={labelCls} data-id="element-1998">Diameter ({unit})</label>
              <input type="number" value={diameter} onChange={e => setDiameter(e.target.value)} placeholder="0" className={inputCls} min="0" step="any" data-id="element-1999" />
            </div>
            <div data-id="element-2000">
              <label className={labelCls} data-id="element-2001">Depth ({depthUnit})</label>
              <input type="number" value={depth} onChange={e => setDepth(e.target.value)} placeholder="0" className={inputCls} min="0" step="any" data-id="element-2002" />
            </div>
          </motion.div>}
        {shape === 'tri' && <motion.div key="tri" initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} className="grid grid-cols-3 gap-3" data-id="element-2003">
            <div data-id="element-2004">
              <label className={labelCls} data-id="element-2005">Base ({unit})</label>
              <input type="number" value={base} onChange={e => setBase(e.target.value)} placeholder="0" className={inputCls} min="0" step="any" data-id="element-2006" />
            </div>
            <div data-id="element-2007">
              <label className={labelCls} data-id="element-2008">Height ({unit})</label>
              <input type="number" value={height} onChange={e => setHeight(e.target.value)} placeholder="0" className={inputCls} min="0" step="any" data-id="element-2009" />
            </div>
            <div data-id="element-2010">
              <label className={labelCls} data-id="element-2011">Depth ({depthUnit})</label>
              <input type="number" value={depth} onChange={e => setDepth(e.target.value)} placeholder="0" className={inputCls} min="0" step="any" data-id="element-2012" />
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Live result + apply CTA — appears only once we have a real number */}
      <AnimatePresence data-id="element-2013">
        {canApply && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="overflow-hidden" data-id="element-2014">
            <div className="mt-5 pt-4 border-t border-[#e5e5e5] dark:border-[#4a4a4a] flex items-center justify-between gap-3" data-id="element-2015">
              <div data-id="element-2016">
                <p className="text-xs text-stone-500 dark:text-stone-400 uppercase font-bold tracking-wider" data-id="element-2017">
                  Estimate
                </p>
                <p className="text-lg font-extrabold text-stone-900 dark:text-white" data-id="element-2018">
                  {yards} yd
                  <span className="text-sm font-medium text-stone-500 dark:text-stone-400 ml-2" data-id="element-2019">
                    ≈ {tons} tons
                  </span>
                </p>
              </div>
              <button type="button" onClick={() => onApply(yards)} className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-[#3d7530] hover:bg-[#2d5824] text-white text-sm font-bold rounded-full transition-colors" data-id="element-2020">
                Use {Math.ceil(yards)} yd
                <ArrowRight className="w-4 h-4" data-id="element-2021" />
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}